
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  FileText, 
  Eye, 
  Download, 
  Calendar, 
  Upload,
  ChevronDown,
  User
} from 'lucide-react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';

// Mock data for medical documents
const MEDICAL_DOCUMENTS = [
  {
    id: '1',
    patientId: 1,
    patientName: 'Emily Johnson',
    documentName: 'X-Ray Results',
    documentType: 'X-Ray',
    uploadDate: '2023-10-15',
    uploadedBy: 'Dr. Michael Chen',
    sessionId: '101',
    status: 'reviewed',
    thumbnailUrl: '/placeholder.svg'
  },
  {
    id: '2',
    patientId: 2,
    patientName: 'Robert Davis',
    documentName: 'Blood Test Results',
    documentType: 'Lab Results',
    uploadDate: '2023-10-12',
    uploadedBy: 'Dr. Sarah Miller',
    sessionId: '102',
    status: 'pending',
    thumbnailUrl: '/placeholder.svg'
  },
  {
    id: '3',
    patientId: 3,
    patientName: 'Jennifer Martinez',
    documentName: 'Medical History',
    documentType: 'Clinical Notes',
    uploadDate: '2023-10-08',
    uploadedBy: 'Dr. Sarah Miller',
    sessionId: '103',
    status: 'reviewed',
    thumbnailUrl: '/placeholder.svg'
  },
  {
    id: '4',
    patientId: 4,
    patientName: 'David Wilson',
    documentName: 'Allergy Test Results',
    documentType: 'Lab Results',
    uploadDate: '2023-10-05',
    uploadedBy: 'Dr. Michael Chen',
    sessionId: '104',
    status: 'pending',
    thumbnailUrl: '/placeholder.svg'
  },
  {
    id: '5',
    patientId: 5,
    patientName: 'Sophia Lee',
    documentName: 'CT Scan Images',
    documentType: 'CT Scan',
    uploadDate: '2023-10-03',
    uploadedBy: 'Dr. Sarah Miller',
    sessionId: '105',
    status: 'reviewed',
    thumbnailUrl: '/placeholder.svg'
  }
];

const MedicalDocumentsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [documents, setDocuments] = useState(MEDICAL_DOCUMENTS);

  const handleViewSession = (sessionId: string) => {
    navigate(`/doctor-area/treatment-sessions?sessionId=${sessionId}`);
    
    toast({
      title: "Session Details",
      description: `Viewing session #${sessionId}`,
    });
  };

  const handleViewPatient = (patientId: number, patientName: string) => {
    navigate(`/doctor-area/case-file/${patientId}`);
    
    toast({
      title: "Patient Profile",
      description: `Viewing profile for ${patientName}`,
    });
  };

  const handleDownloadDocument = (docId: string, docName: string) => {
    // This would download the document in a real implementation
    toast({
      title: "Document Download",
      description: `Downloading ${docName}`,
    });
  };

  const handleViewDocument = (docId: string, docName: string) => {
    // This would show a document preview in a real implementation
    toast({
      title: "Document Preview",
      description: `Viewing ${docName}`,
    });
  };
  
  const filteredDocuments = documents.filter(doc => 
    doc.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.documentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.documentType.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Medical Documents</h1>
      
      <div className="grid gap-6">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <CardTitle>Document Management</CardTitle>
                <CardDescription>
                  View and manage patient medical records and documents
                </CardDescription>
              </div>
              <Button>
                <Upload className="mr-2 h-4 w-4" />
                Upload New Document
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by patient, document name or type..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    Filter Documents
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px]">
                  <DropdownMenuItem onClick={() => setDocuments(MEDICAL_DOCUMENTS)}>
                    All Documents
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDocuments(MEDICAL_DOCUMENTS.filter(doc => doc.status === 'pending'))}>
                    Pending Review
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDocuments(MEDICAL_DOCUMENTS.filter(doc => doc.status === 'reviewed'))}>
                    Reviewed
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Tabs defaultValue="list">
              <TabsList className="mb-4">
                <TabsTrigger value="list">List View</TabsTrigger>
                <TabsTrigger value="grid">Grid View</TabsTrigger>
              </TabsList>
              
              <TabsContent value="list">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Patient</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDocuments.length > 0 ? (
                        filteredDocuments.map((document) => (
                          <TableRow key={document.id}>
                            <TableCell className="font-medium">{document.documentName}</TableCell>
                            <TableCell>
                              <button 
                                className="hover:underline text-left"
                                onClick={() => handleViewPatient(document.patientId, document.patientName)}
                              >
                                {document.patientName}
                              </button>
                            </TableCell>
                            <TableCell>{document.documentType}</TableCell>
                            <TableCell>{new Date(document.uploadDate).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Badge
                                variant={document.status === 'reviewed' ? 'default' : 'outline'}
                              >
                                {document.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewSession(document.sessionId)}
                                  title="View Session"
                                >
                                  <Calendar className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewPatient(document.patientId, document.patientName)}
                                  title="View Patient"
                                >
                                  <User className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewDocument(document.id, document.documentName)}
                                  title="View Document"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadDocument(document.id, document.documentName)}
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="h-24 text-center">
                            No documents found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              
              <TabsContent value="grid">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredDocuments.length > 0 ? (
                    filteredDocuments.map((document) => (
                      <Card key={document.id} className="overflow-hidden">
                        <div className="h-40 bg-muted flex items-center justify-center">
                          <FileText className="h-20 w-20 text-muted-foreground/60" />
                        </div>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-medium truncate">{document.documentName}</h3>
                            <Badge
                              variant={document.status === 'reviewed' ? 'default' : 'outline'}
                              className="ml-2 shrink-0"
                            >
                              {document.status === 'reviewed' ? 'Reviewed' : 'Pending'}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-1 truncate">
                            Patient: {document.patientName}
                          </p>
                          <p className="text-sm text-muted-foreground mb-3 truncate">
                            Date: {new Date(document.uploadDate).toLocaleDateString()}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleViewSession(document.sessionId)}
                            >
                              <Calendar className="h-3 w-3 mr-1" />
                              Session
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => handleViewPatient(document.patientId, document.patientName)}
                            >
                              <User className="h-3 w-3 mr-1" />
                              Patient
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1"
                              onClick={() => handleViewDocument(document.id, document.documentName)}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                      No documents found.
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MedicalDocumentsPage;
